import React, { useState } from "react";
import {
  Button,
  Box,
  Text,
  Input,
  FormControl,
  Tooltip,
  useColorMode,
  Flex,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  CheckIcon,
  CloseIcon,
  EmailIcon,
} from "@chakra-ui/icons";
import { trackNewsletterSubscribed } from "../../utils/analytics";
import { getCurrentColor } from "../../constants";
import useHttp from "../../hooks/useHttp";

const MainPageNewsLetter = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");

  const { colorMode } = useColorMode();
  const { postNewsLetter } = useHttp();

  const [subscribeStatus, setSubscribeStatus] = useState(null);

  const handleSubscribe = (e, email) => {
    e.preventDefault();
    setIsSubmitting(true);

    postNewsLetter({ email })
      .then((res) => setSubscribeStatus(res))
      .catch((err) => setSubscribeStatus(err))
      .finally(() => {
        setIsSubmitting(false);
        setEmail("");

        setTimeout(() => setSubscribeStatus(null), 5000);
      });

    trackNewsletterSubscribed();
  };

  return (
    <Box maxW="7xl" mx="auto" px={4}>
      <Box
        bg={getCurrentColor(colorMode, "whiteC.100", "gray.800")}
        position="relative"
        rounded="2xl"
        onClick={(e) => e.stopPropagation()}
        overflow="hidden"
        w="full"
        shadow="2xl"
      >
        <Box
          position="absolute"
          top="0"
          right="0"
          w={40}
          h={40}
          bgGradient="linear(to-br, blue.500, purple.600)"
          borderRadius="full"
          opacity={0.2}
          transform="translateY(-50%) translateX(50%)"
          filter={"blur(30px)"}
        />
        <Box
          position="absolute"
          bottom="0"
          left="0"
          w={40}
          h={40}
          bgGradient="linear(to-tr, blue.600, cyan.400)"
          borderRadius="full"
          opacity={0.2}
          transform="translateY(50%) translateX(-50%)"
          filter={"blur(30px)"}
        />

        <Box p={8} position="relative" zIndex={0}>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={8}
          >
            <Box>
              <Box mb={8}>
                <Text
                  fontSize="3xl"
                  fontWeight="bold"
                  color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
                >
                  Sign up for our Newsletter
                </Text>
                <Text color="blueC.100" fontSize="sm">
                  Stay informed with Bullet Scout
                </Text>
              </Box>

              <Box mb={6}>
                <Text
                  color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
                  mb={4}
                  textAlign="start"
                  fontSize="md"
                  lineHeight="1.5"
                >
                  Subscribe to our newsletter and get the latest ammunition
                  news, deals, and insights delivered directly to your inbox.
                </Text>

                <Box
                  display="grid"
                  gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }}
                  gap={3}
                >
                  <Tooltip
                    label="Get instant notifications when ammunition prices drop below your specified thresholds."
                    aria-label="Price drop alerts"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "gray.300",
                      )}
                      fontSize="sm"
                    >
                      <CheckIcon color="green.500" width={4} height={4} />
                      <Text ml={2}>Price drop alerts</Text>
                    </Box>
                  </Tooltip>

                  <Tooltip
                    label="Access special offers and discounts available only to newsletter subscribers."
                    aria-label="Exclusive deals"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "gray.300",
                      )}
                      fontSize="sm"
                    >
                      <CheckIcon color="green.500" width={4} height={4} />
                      <Text ml={2}>Exclusive deals</Text>
                    </Box>
                  </Tooltip>

                  <Tooltip
                    label="Stay informed about ammunition restocks and availability."
                    aria-label="Inventory updates"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "gray.300",
                      )}
                      fontSize="sm"
                    >
                      <CheckIcon color="green.500" width={4} height={4} />
                      <Text ml={2}>Inventory updates</Text>
                    </Box>
                  </Tooltip>

                  <Tooltip
                    label="Be the first to know about new ammunition products and brands."
                    aria-label="New product releases"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "gray.300",
                      )}
                      fontSize="sm"
                    >
                      <CheckIcon color="green.500" width={4} height={4} />
                      <Text ml={2}>New product releases</Text>
                    </Box>
                  </Tooltip>

                  <Tooltip
                    label="Get updates on ammunition regulations and other industry news."
                    aria-label="Industry news"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "gray.300",
                      )}
                      fontSize="sm"
                    >
                      <CheckIcon color="green.500" width={4} height={4} />
                      <Text ml={2}>Industry news</Text>
                    </Box>
                  </Tooltip>

                  <Tooltip
                    label="Get personalized ammunition suggestions based on your preferences."
                    aria-label="Personalized recommendations"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "gray.300",
                      )}
                      fontSize="sm"
                    >
                      <CheckIcon color="green.500" width={4} height={4} />
                      <Text textAlign="start" ml={2}>
                        Personalized recommendations
                      </Text>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" justifyContent="center">
              <Box
                bg={getCurrentColor(colorMode, "whiteC.100", "gray.800")}
                p={8}
                rounded="xl"
                borderWidth="1px"
                borderColor={getCurrentColor(
                  colorMode,
                  "blueC.800",
                  "gray.700",
                )}
              >
                {subscribeStatus === null ? (
                  <form onSubmit={(e) => handleSubscribe(e, email, false)}>
                    <FormControl id="email" mb={4}>
                      <Input
                        type="email"
                        placeholder="Your email address"
                        value={email}
                        onChange={({ target }) => setEmail(target.value)}
                        required
                        bg={getCurrentColor(
                          colorMode,
                          "whiteC.100",
                          "gray.700",
                        )}
                        borderColor={getCurrentColor(
                          colorMode,
                          "grayC.500",
                          "grayC.2200",
                        )}
                        paddingLeft={8}
                        zIndex={1}
                        _focus={{
                          borderColor: "blue.500",
                          ring: 1,
                          ringColor: "blue.500",
                        }}
                      />
                      <EmailIcon
                        zIndex={2}
                        position="absolute"
                        top="50%"
                        left={2}
                        transform="translate(0%, -50%)"
                        color="grayC.4000"
                      />
                    </FormControl>

                    <Button
                      type="submit"
                      color="whiteC.100"
                      bg="blueC.100"
                      isLoading={isSubmitting}
                      w="full"
                      mb={4}
                      rightIcon={<ArrowForwardIcon />}
                    >
                      Subscribe Now
                    </Button>

                    <Text color="gray.500" fontSize="xs" textAlign="center">
                      We respect your privacy. Unsubscribe at any time.
                    </Text>
                  </form>
                ) : subscribeStatus.status === "success" ? (
                  <Flex
                    flexDirection={"column"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"10px"}
                  >
                    <CheckIcon fontSize={"40px"} color={"green.500"} />
                    <Text fontSize={"20px"} color={"green.500"}>
                      {subscribeStatus.message}
                    </Text>
                  </Flex>
                ) : (
                  <Flex
                    flexDirection={"column"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"10px"}
                  >
                    <CloseIcon fontSize={"40px"} color={"red.500"} />
                    <Text fontSize={"20px"} color={"red.500"}>
                      {subscribeStatus.errors &&
                        Object.entries(subscribeStatus.errors).map(
                          ([field, messages]) => (
                            <Text
                              key={field}
                              color="red.500"
                              fontSize="sm"
                              mt={1}
                            >
                              {Array.isArray(messages) ? messages[0] : messages}
                            </Text>
                          ),
                        )}
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainPageNewsLetter;
